import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AGENT_SCOPE_FIELD_OPERATORS,
  AgentScopeField,
  AgentScopeOperator,
  IAgentScopeCondition,
  IAgentScopeGroup,
} from 'shared';

import { Button } from '../../../../common/Atoms/Button';
import { Badge } from '../../../../common/Atoms/Badge';
import { Card } from '../../../../common/Atoms/Card';
import { Select } from '../../../../common/Atoms/Select';
import { SmallSpinner } from '../../../../common/Atoms/SmallSpinner';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { useContactTags } from '../../../hooks/useContactTags';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useGroups } from '../../../hooks/useGroups';
import { useGetAgentSettingsQuery, useUpdateAgentSettingsMutation } from '../../../services/api/aiAgentApi/aiAgent';

const defaultCondition = (): IAgentScopeCondition => ({
  field: AgentScopeField.GROUP,
  operator: AGENT_SCOPE_FIELD_OPERATORS[AgentScopeField.GROUP][0],
  value: ``,
});

const defaultGroup = (): IAgentScopeGroup => {
  return {
    conditions: [defaultCondition()],
  };
};

const scopeFieldOptions = [
  {
    label: `Group`,
    value: AgentScopeField.GROUP,
  },
  {
    label: `Tag`,
    value: AgentScopeField.TAG,
  },
];

const scopeOperatorLabels: Record<AgentScopeOperator, string> = {
  [AgentScopeOperator.IS]: `is`,
  [AgentScopeOperator.IS_NOT]: `is not`,
  [AgentScopeOperator.INCLUDES]: `includes`,
  [AgentScopeOperator.NOT_INCLUDES]: `does not include`,
};

export function AgentSettingsPage() {
  const currentOrg = useGetSelectedOrganisation();
  const organisationId = currentOrg?.id;
  const { tags } = useContactTags();
  const { data: groups = [] } = useGroups();

  const {
    data: settings,
    isLoading,
    error,
  } = useGetAgentSettingsQuery({
    organisationId: organisationId || ``,
  }, {
    skip: !organisationId,
  });

  const [updateAgentSettings, { isLoading: isUpdatingSettings }] = useUpdateAgentSettingsMutation();
  const [scopeRules, setScopeRules] = useState<IAgentScopeGroup[]>([]);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [pendingSection, setPendingSection] = useState<`enabled` | `scope` | null>(null);

  useEffect(() => {
    if (!settings) {
      return;
    }

    setScopeRules(settings.scopeRules || []);
    setScopeError(null);
  }, [settings]);

  const groupOptions = useMemo(() => {
    return groups.map(group => ({
      label: group.title,
      value: String(group.id),
    }));
  }, [groups]);

  const tagOptions = useMemo(() => {
    return tags.map(tag => ({
      label: tag,
      value: tag,
    }));
  }, [tags]);

  // ponytail: JSON stringify is the smallest safe deep compare for this small form state.
  const hasScopeChanges = !!settings && JSON.stringify(settings.scopeRules || []) !== JSON.stringify(scopeRules);
  const hasIncompleteScopeRules = scopeRules.some(group => group.conditions.some(condition => !condition.value.trim()));
  const isAgentEnabled = settings?.enabled || false;
  const isUpdatingEnabled = isLoading || (isUpdatingSettings && pendingSection === `enabled`);
  const isUpdatingScope = isUpdatingSettings && pendingSection === `scope`;
  const isToggleDisabled = !settings || isUpdatingSettings;

  const updateCondition = (groupIndex: number, conditionIndex: number, condition: IAgentScopeCondition) => {
    setScopeError(null);

    setScopeRules(prev => prev.map((group, currentGroupIndex) => {
      if (groupIndex !== currentGroupIndex) {
        return group;
      }

      return {
        ...group,
        conditions: group.conditions.map((currentCondition, currentConditionIndex) => currentConditionIndex === conditionIndex ? condition : currentCondition),
      };
    }));
  };

  const addCondition = (groupIndex: number) => {
    setScopeError(null);

    setScopeRules(prev => prev.map((group, currentGroupIndex) => {
      if (groupIndex !== currentGroupIndex) {
        return group;
      }

      return {
        ...group,
        conditions: [...group.conditions, defaultCondition()],
      };
    }));
  };

  const removeCondition = (groupIndex: number, conditionIndex: number) => {
    setScopeError(null);

    setScopeRules(prev => {
      const next = prev.map((group, currentGroupIndex) => {
        if (groupIndex !== currentGroupIndex) {
          return group;
        }

        return {
          ...group,
          conditions: group.conditions.filter((_, currentConditionIndex) => currentConditionIndex !== conditionIndex),
        };
      }).filter(group => group.conditions.length > 0);

      return next;
    });
  };

  const addGroup = () => {
    setScopeError(null);
    setScopeRules(prev => [...prev, defaultGroup()]);
  };

  const handleEnabledChange = async(enabled: boolean) => {
    if (!organisationId) {
      return;
    }

    setPendingSection(`enabled`);
    try {
      await updateAgentSettings({
        organisationId,
        enabled,
      }).unwrap();
    }
    finally {
      setPendingSection(null);
    }
  };

  const handleSaveScopeRules = async() => {
    if (!organisationId) {
      return;
    }

    if (hasIncompleteScopeRules) {
      setScopeError(`Complete every rule before saving.`);

      return;
    }

    setPendingSection(`scope`);
    try {
      await updateAgentSettings({
        organisationId,
        scopeRules,
      }).unwrap();
    }
    finally {
      setPendingSection(null);
    }
  };

  if (!organisationId) {
    return null;
  }

  return (
    <div className={ `w-full space-y-4` }>
      <div className={ `space-y-1 px-1` }>
        <Paragraph
          as={ `h2` }
          className={ `text-lg font-semibold text-slate-900` }
        >
          { `Agent Settings` }
        </Paragraph>
        <Paragraph
          size={ `sm` }
          variant={ `secondary` }
          className={ `text-slate-600` }
        >
          { `Configure when automation runs and define which contacts are in scope.` }
        </Paragraph>
      </div>

      {
        !!error &&
        <Paragraph variant={ `error` }>
          { `Failed to load agent settings.` }
        </Paragraph>
      }

      <Card className={ `space-y-4 border border-slate-200 bg-white! shadow-none! px-4! py-5! sm:px-6!` }>
        <div className={ `flex flex-col gap-3 md:flex-row md:items-start md:justify-between` }>
          <div className={ `space-y-1` }>
            <Paragraph
              as={ `h3` }
              className={ `text-base font-semibold text-slate-900` }
            >
              { `Automation Control` }
            </Paragraph>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
              className={ `text-slate-600` }
            >
              { `Enable or pause AI actions across this organisation with immediate effect.` }
            </Paragraph>
          </div>

          <Badge
            color={ isAgentEnabled ? `green` : `gray` }
            size={ `s` }
            message={ isAgentEnabled ? `Enabled` : `Disabled` }
            className={ `self-start border border-slate-200 md:self-center` }
          />
        </div>

        <div className={ `flex items-center justify-between gap-4 border-t border-slate-200 pt-4` }>
          <div className={ `space-y-1` }>
            <Paragraph className={ `font-semibold text-slate-900` }>
              { `AI agent enabled` }
            </Paragraph>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
              className={ `max-w-xl text-slate-600` }
            >
              { `When enabled, the agent can run automatically for eligible contacts. Changes apply instantly.` }
            </Paragraph>
          </div>

          <div className={ `flex items-center gap-3` }>
            {
              isUpdatingEnabled &&
              <SmallSpinner colored />
            }

            <button
              type={ `button` }
              role={ `switch` }
              aria-checked={ isAgentEnabled }
              aria-label={ `Toggle AI agent enabled` }
              disabled={ isToggleDisabled }
              onClick={ () => handleEnabledChange(!isAgentEnabled) }
              className={ `${isToggleDisabled ? `cursor-not-allowed opacity-60` : `cursor-pointer`} relative inline-flex h-8 w-14 items-center rounded-full border border-slate-300 bg-slate-200 p-0.5 transition-colors duration-200 ease-out motion-reduce:transition-none focus:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-600 focus-visible:ring-offset-2 ${isAgentEnabled ? `border-paidniceGreen-700 bg-paidniceGreen-700 hover:bg-paidniceGreen-600` : `hover:bg-slate-300`}` }
            >
              <span
                aria-hidden={ `true` }
                className={ `inline-block h-6 w-6 rounded-full bg-white shadow-xs transition-transform duration-200 ease-out motion-reduce:transition-none ${isAgentEnabled ? `translate-x-6` : `translate-x-0`}` }
              />
            </button>
          </div>
        </div>
      </Card>

      <Card className={ `space-y-4 border border-slate-200 bg-white! shadow-none! px-4! py-5! sm:px-6!` }>
        <div className={ `space-y-2` }>
          <div className={ `flex items-center justify-between gap-3` }>
            <Paragraph
              as={ `h3` }
              className={ `text-base font-semibold text-slate-900` }
            >
              { `Contact Scope` }
            </Paragraph>
            {
              hasScopeChanges &&
              <Badge
                color={ `yellow` }
                size={ `xs` }
                message={ `Unsaved` }
              />
            }
          </div>
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
            className={ `text-slate-600` }
          >
            { `Define which contacts the AI agent can operate on.` }
          </Paragraph>
        </div>

        {
          scopeRules.length === 0 &&
          <div className={ `flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-slate-300 bg-slate-50/70 px-6 py-8 text-center` }>
            <Paragraph
              size={ `sm` }
              variant={ `secondary` }
              className={ `max-w-xl text-slate-700` }
            >
              { `No scope rules - the agent applies to all contacts.` }
            </Paragraph>
            <Button
              color={ `gray` }
              className={ `cursor-pointer transition-colors duration-200` }
              icon={ <PlusIcon className={ `ml-1 h-4 w-4` } /> }
              disabled={ !settings || isUpdatingSettings }
              onClick={ addGroup }
            >
              { `Add segment` }
            </Button>
          </div>
        }

        {
          scopeRules.length > 0 &&
          <div className={ `space-y-3` }>
            {
              scopeRules.map((group, groupIndex) => (
                <Fragment key={ `scope-group-${groupIndex}` }>
                  <div className={ `space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4` }>
                    {
                      group.conditions.map((condition, conditionIndex) => {
                        const operatorOptions = AGENT_SCOPE_FIELD_OPERATORS[condition.field].map(operator => ({
                          label: scopeOperatorLabels[operator],
                          value: operator,
                        }));
                        const valueOptions = condition.field === AgentScopeField.GROUP ? groupOptions : tagOptions;
                        const isConditionIncomplete = !condition.value.trim();

                        return (
                          <div
                            key={ `scope-condition-${groupIndex}-${conditionIndex}` }
                            className={ `space-y-2` }
                          >
                            {
                              conditionIndex > 0 &&
                              <Paragraph
                                size={ `xs` }
                                className={ `w-fit rounded-full bg-white px-2 py-1 text-[11px] font-semibold tracking-wide text-slate-600` }
                              >
                                { `AND` }
                              </Paragraph>
                            }
                            <div>
                              <div className={ `grid grid-cols-1 items-end gap-2 xl:grid-cols-[1fr_1fr_1fr_auto]` }>
                                <Select
                                  options={ scopeFieldOptions }
                                  selected={ condition.field }
                                  onChange={ ({ value }) => {
                                    const nextField = value as AgentScopeField;

                                    updateCondition(groupIndex, conditionIndex, {
                                      field: nextField,
                                      operator: AGENT_SCOPE_FIELD_OPERATORS[nextField][0],
                                      value: ``,
                                    });
                                  } }
                                  label={ `Field` }
                                  valueClassname={ `text-slate-700` }
                                  disabled={ !settings || isUpdatingSettings }
                                />
                                <Select
                                  options={ operatorOptions }
                                  selected={ condition.operator }
                                  onChange={ ({ value }) => {
                                    updateCondition(groupIndex, conditionIndex, {
                                      ...condition,
                                      operator: value as AgentScopeOperator,
                                    });
                                  } }
                                  label={ `Operator` }
                                  valueClassname={ `text-slate-700` }
                                  disabled={ !settings || isUpdatingSettings }
                                />
                                <div className={ `space-y-1` }>
                                  <Select
                                    options={ valueOptions }
                                    selected={ condition.value || null }
                                    onChange={ ({ value }) => {
                                      updateCondition(groupIndex, conditionIndex, {
                                        ...condition,
                                        value: String(value || ``),
                                      });
                                    } }
                                    label={ condition.field === AgentScopeField.GROUP ? `Group value` : `Tag value` }
                                    valueClassname={ `text-slate-700` }
                                    emptyText={ condition.field === AgentScopeField.GROUP ? `Select group` : `Select tag` }
                                    disabled={ !settings || isUpdatingSettings || valueOptions.length === 0 }
                                    buttonClassname={ `${isConditionIncomplete ? `border-amber-500! bg-amber-100/80!` : ``}` }
                                  />
                                </div>
                                <button
                                  type={ `button` }
                                  aria-label={ `Remove condition` }
                                  className={ `cursor-pointer rounded-md border border-slate-200 bg-white p-2 text-slate-500 transition-colors duration-200 hover:border-slate-300 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60` }
                                  disabled={ !settings || isUpdatingSettings }
                                  onClick={ () => removeCondition(groupIndex, conditionIndex) }
                                >
                                  <XMarkIcon className={ `h-4 w-4` } />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    }
                    <div className={ `flex justify-center border-t border-slate-200 pt-3` }>
                      <Button
                        color={ `gray` }
                        className={ `cursor-pointer transition-colors duration-200` }
                        icon={ <PlusIcon className={ `ml-1 h-4 w-4` } /> }
                        disabled={ !settings || isUpdatingSettings }
                        onClick={ () => addCondition(groupIndex) }
                      >
                        { `Add condition` }
                      </Button>
                    </div>
                  </div>
                  {
                    groupIndex < scopeRules.length - 1 &&
                    <div className={ `flex justify-center py-1` }>
                      <Paragraph
                        size={ `xs` }
                        className={ `rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold tracking-wide text-slate-600` }
                      >
                        { `OR` }
                      </Paragraph>
                    </div>
                  }
                </Fragment>
              ))
            }
            <div className={ `flex justify-center pb-1 pt-2` }>
              <Button
                color={ `gray` }
                className={ `cursor-pointer transition-colors duration-200` }
                icon={ <PlusIcon className={ `ml-1 h-4 w-4` } /> }
                disabled={ !settings || isUpdatingSettings }
                onClick={ addGroup }
              >
                { `Add segment` }
              </Button>
            </div>
          </div>
        }

        <div className={ `flex items-center justify-between border-t border-slate-200 pt-3` }>
          {
            !!scopeError &&
            <Paragraph
              size={ `xs` }
              variant={ `error` }
              className={ `text-red-700` }
            >
              { scopeError }
            </Paragraph>
          }
          {
            !scopeError && hasIncompleteScopeRules &&
            <Paragraph
              size={ `xs` }
              variant={ `warning` }
              className={ `text-amber-700` }
            >
              { `Complete or remove the highlighted condition before saving.` }
            </Paragraph>
          }
          <Button
            loading={ isUpdatingScope }
            disabled={ !settings || !hasScopeChanges || hasIncompleteScopeRules || isUpdatingSettings }
            className={ `ml-auto` }
            onClick={ handleSaveScopeRules }
          >
            { `Save scope settings` }
          </Button>
        </div>
      </Card>
    </div>
  );
}
